// ============================================================
// KICKOFF DECK
//
// Slide templates drawn from the five real decks (B-Rooted, Behavioral
// Medicine, ADD Excellence, True Haven, Healing Life), plus the two things
// none of them had: goals with numbers, and a timeline with dates.
//
// Every slide is optional and editable. Picking a package preselects a
// sensible set — an SEO-only kickoff shouldn't open with website timelines.
// ============================================================

// Two different audiences, two different strings:
//   purpose  — internal. Why the slide exists. Only ever shown in the builder.
//   subtitle — what the CLIENT reads under the heading during the meeting.
// Getting these confused is how "fill this in live on the call" ends up on a
// screen the client is looking at.
//
// A field is one editable line or block on a slide.
//   text     — single line
//   lines    — a bullet list, one per line
//   longtext — paragraph
//   date     — date picker
export const SLIDE_TEMPLATES = [
  {
    key: 'agenda',
    name: 'Agenda',
    subtitle: 'Here\'s how we\'ll use our time today.',
    always: true,
    purpose: 'Sets the shape of the meeting so nobody is waiting for a topic that never comes.',
    fields: [
      { key: 'items', label: 'Agenda items', type: 'lines', default:
`Welcome to LMR
Dashboard & brand review
Goals and what success looks like
Strategy & timeline
Housekeeping and next steps` },
    ],
  },
  {
    key: 'welcome',
    name: 'Welcome to LMR',
    subtitle: 'Who you\'re working with, how often we\'ll talk, and how to reach us in between.',
    always: true,
    purpose: 'Who they are working with, how often, and how to reach you between meetings.',
    fields: [
      { key: 'ad', label: 'Account Director', type: 'text', auto: 'ad' },
      { key: 'am', label: 'Account Manager', type: 'text', auto: 'am' },
      { key: 'meeting_rhythm', label: 'What meetings look like', type: 'text',
        default: '30 minutes, every week for the first month, then every other week' },
      { key: 'between', label: 'Communication between meetings', type: 'text',
        default: 'Email your AD any time — we reply same or next business day' },
      { key: 'first_weeks', label: 'What we are working on first', type: 'lines' },
      // Said at every kickoff, and the single most useful expectation to set
      // early — it's where "no results" complaints usually come from.
      { key: 'leads_vs_sales', label: 'Leads vs sales', type: 'longtext',
        default:
          'Our job is to optimize the ads for the best leads at the right cost per lead. ' +
          'Your job is to convert them into clients.' },
      { key: 'leads_vs_sales_points', label: 'What that means in practice', type: 'lines',
        default:
`How quickly you respond changes your conversion more than anything we do
If inquiries are coming in but not converting, tell us — that is a different problem and we can help
We will always show you what came in, so you can see it working` },
    ],
  },
  {
    key: 'goals',
    name: 'Goals & what success looks like',
    subtitle: 'What we\'re aiming for together, and how we\'ll know we got there.',
    always: true,
    isGoals: true,   // pulls from client_goals rather than free text
    purpose:
      'The slide none of the old decks had. Every 30/60/90 check-in measures against this, ' +
      'and a goal without a number and a date cannot be checked.',
    fields: [
      { key: 'intro', label: 'Framing line', type: 'text',
        default: "Here's what we're aiming for, and how we'll know we got there." },
    ],
  },
  {
    key: 'expectations',
    name: 'What to expect, month by month',
    subtitle: 'What happens when — including what you won\'t see straight away, and why.',
    always: true,
    purpose:
      'The single most churn-preventing slide. Says plainly what they will NOT see early — ' +
      'which is what stops a month-two "no results" conversation.',
    fields: [
      { key: 'month1', label: 'Month 1 — what we do / what you will see', type: 'lines' },
      { key: 'month2_3', label: 'Months 2-3', type: 'lines' },
      { key: 'month4_6', label: 'Months 4-6', type: 'lines' },
      { key: 'not_yet', label: 'What you will NOT see yet, and why', type: 'lines',
        default:
`SEO rankings take 4-6 months — Google re-crawls slowly
Ads spend the first 2-4 weeks in learning phase
Social audiences build over months, not weeks` },
    ],
  },
  {
    key: 'timeline',
    name: 'Timeline',
    subtitle: 'When each piece lands.',
    purpose: 'When each piece lands. Tracked on the client record so we can see later whether we hit them.',
    isTimeline: true,
    fields: [
      { key: 'intro', label: 'Framing line', type: 'text',
        default: 'Here is what lands when.' },
    ],
  },
  {
    key: 'dashboard',
    name: 'Dashboard & brand review',
    subtitle: 'Your dashboard, and the brand questions that shape everything we build for you.',
    always: true,
    isBrand: true,   // edits write straight to the client record
    purpose:
      'Fill this in live on the call — it saves to the client profile, so there is nothing '
      + 'to write up afterwards.',
    fields: [
      { key: 'dashboard_url', label: 'Dashboard link', type: 'text' },
      { key: 'brand_note', label: 'Why this matters', type: 'longtext',
        default:
          'This drives the tone of the copy, the placement of color, and the overall feel of ' +
          'everything we build — ads, landing pages, web pages, social content and blogs.' },
    ],
  },
  {
    key: 'strategy_website',
    name: 'Strategy — Website',
    subtitle: 'How we\'ll approach your website, and what it means for the other work.',
    optional: true,
    purpose: 'Add only if this client needs website specifics called out.',
    fields: [
      { key: 'next_steps', label: 'Next steps', type: 'lines' },
      { key: 'while_building', label: 'What we CAN do while designing', type: 'lines',
        default:
`Content strategy and content
Blogs
Keyword research and SEO prep
Ad prep` },
      { key: 'not_while_building', label: 'What we CANNOT do while designing', type: 'lines',
        default:
`Run ads
Build SEO
Drive new leads` },
    ],
  },
  {
    key: 'strategy_seo',
    name: 'Strategy — SEO',
    subtitle: 'How we\'ll approach your SEO.',
    optional: true,
    purpose: 'Add only if this client needs SEO specifics called out.',
    fields: [
      { key: 'next_steps', label: 'Next steps', type: 'lines',
        default:
`Complete audit and findings, sent to you with our recommendations
Keyword research
Optimize existing pages
Create new pages and begin monthly SEO blogs` },
    ],
  },
  {
    key: 'strategy_ads',
    name: 'Strategy — Ads',
    subtitle: 'How we\'ll approach your ads.',
    optional: true,
    purpose: 'Add only if this client needs ad specifics called out. Leads vs sales is covered in Welcome.',
    fields: [
      { key: 'next_steps', label: 'Next steps', type: 'lines',
        default:
`Determine services to advertise
Keyword prep
Create landing pages
Go live` },
    ],
  },
  {
    key: 'strategy_social',
    name: 'Strategy — Social',
    subtitle: 'How we\'ll approach your social.',
    optional: true,
    purpose: '',
    fields: [{ key: 'next_steps', label: 'Next steps', type: 'lines' }],
  },
  {
    key: 'notes',
    name: 'Notes from the consultation',
    subtitle: 'What you told us matters most — said back to you, so we know we heard it right.',
    purpose: 'Their words from the sales call, repeated back. Shows you were listening.',
    fields: [
      { key: 'what_they_said', label: 'What you told us you want', type: 'longtext' },
      { key: 'tried_before', label: "What you've tried before", type: 'longtext' },
      { key: 'concerns', label: 'What you want to avoid', type: 'longtext' },
    ],
  },
  {
    key: 'housekeeping',
    name: 'Housekeeping & next steps',
    subtitle: 'The practical bits, and what we\'re doing this week.',
    always: true,
    purpose: 'Access, billing, meeting time, and what happens this week.',
    fields: [
      { key: 'meeting_time', label: 'Recurring meeting time', type: 'text' },
      { key: 'next_week', label: 'What we do this week', type: 'lines' },
    ],
  },
]

// The brand identity fields on the clients table, same set and wording as the
// Brand Identity tab on the client profile. Filled in live during the kickoff
// and saved straight to the client record.
export const BRAND_FIELDS = [
  { key: 'brand_logistics', label: 'Logistics',
    hint: 'Insurance, virtual or in person, other operational details' },
  { key: 'brand_personality', label: 'Brand Personality',
    hint: 'If the brand were a person, who would they be? How do they speak?' },
  { key: 'brand_target_audience', label: 'Target Audience',
    hint: 'Diagnoses, identities, life experiences they serve' },
  { key: 'brand_feelings', label: 'Brand Feelings',
    hint: 'What should someone feel landing on their site?' },
  { key: 'brand_differentiators', label: 'Key Differentiators / Value Add',
    hint: 'What makes them stand out from competitors?' },
  { key: 'brand_promise', label: 'Brand Promise',
    hint: 'What they commit to delivering' },
  { key: 'brand_services_modalities', label: 'Services / Modalities',
    hint: 'What the client offers — couples, families, EMDR, group' },
  { key: 'brand_slogan', label: 'Slogan / Repeated Phrases',
    hint: 'Words and phrases to repeat across their messaging' },
]

export const getSlide = k => SLIDE_TEMPLATES.find(s => s.key === k)

// ── Admin checklist ─────────────────────────────────────────────────────────
// Run at the end of the meeting. Each item is Have it / Need it / Not needed,
// so "we never asked" and "they don't have one" stop looking the same.
export const CHECKLIST = [
  { group: 'Access', items: [
    { key: 'website_login',  label: 'Website / CMS login' },
    { key: 'domain',         label: 'Domain registrar access' },
    { key: 'hosting',        label: 'Hosting access' },
    { key: 'gbp',            label: 'Google Business Profile' },
    { key: 'ga4',            label: 'Google Analytics' },
    { key: 'gsc',            label: 'Google Search Console' },
    { key: 'google_ads',     label: 'Google Ads account' },
    { key: 'meta',           label: 'Facebook / Instagram' },
    { key: 'email_platform', label: 'Email platform' },
  ]},
  { group: 'Billing', items: [
    { key: 'card_on_file',   label: 'Card on file for recurring payment' },
    { key: 'ad_billing',     label: 'Billing set up on ad accounts' },
    { key: 'hosting_billing',label: 'Hosting billing' },
  ]},
  { group: 'Content & brand', items: [
    { key: 'info_grab',      label: 'Onboarding Info Grab completed' },
    { key: 'logo_files',     label: 'Logo files' },
    { key: 'brand_guide',    label: 'Brand guide or colors and fonts' },
    { key: 'photos',         label: 'Photos we can use' },
    { key: 'testimonials',   label: 'Testimonials or reviews' },
  ]},
  { group: 'Set up', items: [
    { key: 'meeting_time',   label: 'Recurring meeting time agreed' },
    { key: 'calendly',       label: 'Calendly or booking link' },
    { key: 'contacts',       label: 'Contact info for the site' },
    { key: 'call_tracking',  label: 'Call tracking, if we are measuring calls' },
  ]},
]

export const CHECKLIST_STATES = {
  have:     { label: 'Have it',    color: 'var(--green)',      bg: 'var(--lightgreen-bg)' },
  need:     { label: 'Need it',    color: '#c4962a',           bg: '#fff8e1' },
  na:       { label: 'Not needed', color: 'var(--text-muted)', bg: 'var(--cream)' },
}

export const ALL_CHECKLIST_KEYS = CHECKLIST.flatMap(g => g.items.map(i => i.key))

export const checklistStats = (state = {}) => {
  const vals = ALL_CHECKLIST_KEYS.map(k => state[k]).filter(Boolean)
  const need = ALL_CHECKLIST_KEYS.filter(k => state[k] === 'need')
  return {
    answered: vals.length,
    total: ALL_CHECKLIST_KEYS.length,
    have: vals.filter(v => v === 'have').length,
    need,                                   // the follow-up list
    unanswered: ALL_CHECKLIST_KEYS.filter(k => !state[k]),
  }
}

// Which slides to preselect for a client, from the service flags on their record.
// The running order. Context first, then the consultation notes, then the
// three expectation-setting slides together at the end — that's the part of
// the meeting the whole thing is building toward, so it shouldn't be split up.
export const DEFAULT_ORDER = [
  'agenda',
  'welcome',
  'dashboard',
  'notes',
  'goals',
  'expectations',
  'timeline',
  'housekeeping',
]

// Strategy slides are off by default and added per client when there is
// something specific to say. Everything else starts on.
export const defaultSlidesFor = () => [...DEFAULT_ORDER]

export const DECK_STATUS = {
  draft:     { label: 'Draft',     color: 'var(--text-muted)' },
  ready:     { label: 'Ready',     color: 'var(--gold)' },
  delivered: { label: 'Delivered', color: 'var(--green)' },
}